import { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { INITIAL_STATS, SCENARIOS, buildSessionScenarios } from "@/game/gameData";

const GameContext = createContext(null);

const BASE_STATE = {
  screen: "start",
  player: {
    playerName: "",
    characterName: "",
    businessName: "",
    businessType: "",
    productMission: "",
    audience: "",
    goal: "",
  },
  character: null,
  abilityUsed: false,
  wheelOutcome: null,
  supplies: [],
  stats: { ...INITIAL_STATS },
  month: 1,
  monthChoices: [],
  pendingEvent: null,
  bootstrap: false,
  bootstrapTriggered: false,
  result: null,
  // Session-specific dynamics:
  scenarios: SCENARIOS, // overwritten by freshState() below
  bigIncomeMonth: 9,    // overwritten by freshState() below
  bigIncomeFired: false,
};

function freshState() {
  return {
    ...BASE_STATE,
    scenarios: buildSessionScenarios(),
    bigIncomeMonth: 9 + Math.floor(Math.random() * 4), // 9, 10, 11 or 12
    bigIncomeFired: false,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "GOTO":
      return { ...state, screen: action.screen };
    case "SET_PLAYER":
      return { ...state, player: { ...state.player, ...action.fields } };
    case "PICK_CHARACTER": {
      const char = action.character;
      const boostedStats = applyDeltasLocal(state.stats, char.statBoost || {});
      const finalStats = applyDeltasLocal(boostedStats, char.statPenalty || {});
      return {
        ...state,
        character: char,
        abilityUsed: false,
        stats: finalStats,
        player: { ...state.player, characterName: char.name },
      };
    }
    case "SET_WHEEL": {
      const outcome = action.outcome;
      return {
        ...state,
        wheelOutcome: outcome,
        stats: { ...state.stats, budget: outcome.budget },
      };
    }
    case "TOGGLE_SUPPLY": {
      const id = action.id;
      const has = state.supplies.includes(id);
      return {
        ...state,
        supplies: has ? state.supplies.filter((s) => s !== id) : [...state.supplies, id],
      };
    }
    case "SET_BUDGET":
      return { ...state, stats: { ...state.stats, budget: action.budget } };
    case "APPLY_DELTAS": {
      const next = applyDeltasLocal(state.stats, action.deltas);
      // Bootstrap is recomputed from current budget every tick — it can turn OFF
      // again when income/budget recovers above $0.
      const bootstrap = next.budget <= 0;
      const bootstrapTriggered = state.bootstrapTriggered || bootstrap;
      const adj =
        bootstrap && action.deltas.energy && action.deltas.energy > 0
          ? { ...next, energy: Math.max(0, next.energy - 2) }
          : next;
      return { ...state, stats: adj, bootstrap, bootstrapTriggered };
    }
    case "LOG_CHOICE":
      return { ...state, monthChoices: [...state.monthChoices, action.entry] };
    case "NEXT_MONTH":
      return { ...state, month: state.month + 1 };
    case "SET_PENDING_EVENT":
      return { ...state, pendingEvent: action.event };
    case "CLEAR_EVENT":
      return { ...state, pendingEvent: null };
    case "USE_ABILITY":
      return { ...state, abilityUsed: true };
    case "FIRE_BIG_INCOME":
      return { ...state, bigIncomeFired: true };
    case "SET_RESULT":
      return { ...state, result: action.result };
    case "RESTART":
      return freshState();
    default:
      return state;
  }
}

function applyDeltasLocal(stats, deltas) {
  const next = { ...stats };
  for (const k of Object.keys(deltas)) {
    if (k === "budget") {
      next.budget = Math.max(0, (next.budget || 0) + deltas.budget);
    } else {
      const v = (next[k] || 0) + deltas[k];
      next[k] = Math.max(0, Math.min(100, v));
    }
  }
  return next;
}

export function GameProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, freshState);

  const currentScenario = useMemo(
    () => state.scenarios[state.month - 1] || SCENARIOS[0],
    [state.month, state.scenarios]
  );

  const value = { state, dispatch, currentScenario };

  useEffect(() => {
    // no-op
  }, [state.screen]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be inside GameProvider");
  return ctx;
}
